import { effect, stop } from '@vue/reactivity'
import { evaluateInScope } from '../component/utils'
import qvShow from './qv-show'

export default {

    signature: 'qv-transition',

    handle(node, scope) {

        if (node.nodeType !== Node.ELEMENT_NODE) {
            return null
        }

        if (!node.hasAttribute(qvShow.signature)) {
            return null
        }

        if (node.hasAttribute('qv-for')) {
            return null
        }

        const shouldTransition = this.shouldTransition(node)

        if (!shouldTransition) {
            return null
        }

        const expression = node.getAttribute(qvShow.signature)

        if (!node._originalDisplay) {
            node._originalDisplay = node.style.display !== 'none' ? node.style.display : ''
        }

        let loaded = false

        const runner = effect(() => {

            const condition = evaluateInScope(expression, scope)

            if (condition && node.style.display === 'none') {

                node.style.display = node._originalDisplay
                performEnterTransition(node, this.signature)

            } else if (!condition && node.style.display !== 'none') {

                // Prevent leave transition on initial load
                if (!loaded) {

                    node.style.display = 'none'
                    loaded = true

                } else {

                    performLeaveTransition(node, this.signature, () => {
                        node.style.display = 'none'
                    })

                }

            }

        })

        return () => {
            stop(runner)
        }

    },

    shouldTransition(node) {

        return node.hasAttribute(this.signature) ||
               Array.from(node?.attributes || []).some(attribute => attribute.name.startsWith(`${ this.signature }:`))

    },

}

function getTransitionClasses(node, signature) {

    const transitionClasses = {
        enter: '',
        enterStart: '',
        enterEnd: '',
        leave: '',
        leaveStart: '',
        leaveEnd: '',
    }

    if (!node) {
        return transitionClasses
    }

    // Process all transition attributes
    for (const attribute of Array.from(node?.attributes || [])) {

        if (attribute.name.startsWith(`${ signature }:`)) {

            const name = attribute.name.split(':')[ 1 ]

            if (name === 'enter') {
                transitionClasses.enter = attribute.value
            } else if (name === 'enter-start') {
                transitionClasses.enterStart = attribute.value
            } else if (name === 'enter-end') {
                transitionClasses.enterEnd = attribute.value
            } else if (name === 'leave') {
                transitionClasses.leave = attribute.value
            } else if (name === 'leave-start') {
                transitionClasses.leaveStart = attribute.value
            } else if (name === 'leave-end') {
                transitionClasses.leaveEnd = attribute.value
            }

        }

    }

    // Handle the base case of just qv-transition without any specific classes
    if (node.hasAttribute(`${ signature }`)) {

        if (!transitionClasses.enter.length) {
            transitionClasses.enter = 'transition ease-out duration-300'
        }

        if (!transitionClasses.enterStart.length) {
            transitionClasses.enterStart = 'opacity-0 transform scale-95'
        }

        if (!transitionClasses.enterEnd.length) {
            transitionClasses.enterEnd = 'opacity-100 transform scale-100'
        }

        if (!transitionClasses.leave.length) {
            transitionClasses.leave = 'transition ease-in duration-200'
        }

        if (!transitionClasses.leaveStart.length) {
            transitionClasses.leaveStart = 'opacity-100 transform scale-100'
        }

        if (!transitionClasses.leaveEnd.length) {
            transitionClasses.leaveEnd = 'opacity-0 transform scale-95'
        }

    }

    return transitionClasses

}

function performEnterTransition(node, signature) {

    const transitionClasses = getTransitionClasses(node, signature)

    // If no transition classes, execute callback immediately and return
    if (!transitionClasses.enter && !transitionClasses.enterStart && !transitionClasses.enterEnd) {
        return
    }

    // First, add the enter and enter-start classes
    if (transitionClasses.enter.length) {
        node.classList.add(...transitionClasses.enter.split(' '))
    }

    if (transitionClasses.enterStart.length) {
        node.classList.add(...transitionClasses.enterStart.split(' '))
    }

    // Force a reflow to ensure the initial state is painted
    node.offsetWidth

    // Remove enter-start and add enter-end
    requestAnimationFrame(() => {

        if (transitionClasses.enterStart.length) {
            node.classList.remove(...transitionClasses.enterStart.split(' '))
        }

        if (transitionClasses.enterEnd.length) {
            node.classList.add(...transitionClasses.enterEnd.split(' '))
        }

        // Listen for the transition to end
        node.addEventListener('transitionend', () => {

            if (transitionClasses.enter.length) {
                node.classList.remove(...transitionClasses.enter.split(' '))
            }

            if (transitionClasses.enterEnd.length) {
                node.classList.remove(...transitionClasses.enterEnd.split(' '))
            }

        }, { once: true })

    })

}

function performLeaveTransition(node, signature, callback = null) {

    const transitionClasses = getTransitionClasses(node, signature)

    // If no transition classes, execute callback immediately and return
    if (!transitionClasses.leave && !transitionClasses.leaveStart && !transitionClasses.leaveEnd) {

        if (callback) {
            callback()
        }

        return

    }

    // First, add the leave and leave-start classes
    if (transitionClasses.leave.length) {
        node.classList.add(...transitionClasses.leave.split(' '))
    }

    if (transitionClasses.leaveStart.length) {
        node.classList.add(...transitionClasses.leaveStart.split(' '))
    }

    // Force a reflow to ensure the initial state is painted
    node.offsetWidth

    // Remove leave-start and add leave-end
    requestAnimationFrame(() => {

        if (transitionClasses.leaveStart.length) {
            node.classList.remove(...transitionClasses.leaveStart.split(' '))
        }

        if (transitionClasses.leaveEnd.length) {
            node.classList.add(...transitionClasses.leaveEnd.split(' '))
        }

        // Listen for the transition to end
        node.addEventListener('transitionend', () => {

            // Clean up classes after transition
            if (transitionClasses.leave.length) {
                node.classList.remove(...transitionClasses.leave.split(' '))
            }

            if (transitionClasses.leaveEnd.length) {
                node.classList.remove(...transitionClasses.leaveEnd.split(' '))
            }

            if (callback) {
                callback()
            }

        }, { once: true })

    })

}
